import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react'
import type { ConfigAttention } from '../../lib/configAttention'
import { useI18n } from '../../lib/i18n'

export function ConfigAttentionButton({ attention, onSelect }: {
  attention: ConfigAttention
  onSelect: (section: 'policies' | 'business') => void
}) {
  const { t } = useI18n()
  const tooltipId = useId()
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelClose = () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current)
    closeTimer.current = null
  }
  const closeSoon = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      if (popupRef.current?.contains(document.activeElement)) return
      setPosition(null)
    }, 250)
  }
  const open = position !== null
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const anchor = triggerRef.current?.getBoundingClientRect()
      const popup = popupRef.current?.getBoundingClientRect()
      if (!anchor || !popup) return
      setPosition({
        left: Math.max(12, Math.min(anchor.right + 12, window.innerWidth - popup.width - 12)),
        top: Math.max(12, Math.min(anchor.top + anchor.height / 2 - popup.height / 2, window.innerHeight - popup.height - 12)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null
    if (popupRef.current) observer?.observe(popupRef.current)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      observer?.disconnect()
    }
  }, [open])
  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (target && (triggerRef.current?.contains(target) || popupRef.current?.contains(target))) return
      setPosition(null)
    }
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      // Restore focus before closing so the focus handler cannot reopen it.
      triggerRef.current?.focus()
      setPosition(null)
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  useEffect(() => () => { if (closeTimer.current !== null) clearTimeout(closeTimer.current) }, [])
  const issues: string[] = []
  const { unwritten = 0, holes = 0 } = attention.policies ?? {}
  if (unwritten > 0) issues.push(t('nav.configWarning.policies', { count: unwritten }))
  if (holes > 0) issues.push(t('nav.configWarning.holes', { count: holes }))
  if (attention.business?.missing) issues.push(t('nav.configWarning.business', { count: attention.business.missing }))
  if (!issues.length) return null
  const target = unwritten + holes > 0 ? 'policies' : 'business'
  const action = t(target === 'policies' ? 'nav.configWarning.openPolicies' : 'nav.configWarning.openBusiness')
  const show = (button: HTMLButtonElement) => {
    cancelClose()
    if (open) return
    const rect = button.getBoundingClientRect()
    const width = Math.min(300, window.innerWidth - 24)
    setPosition({
      left: Math.max(12, Math.min(rect.right + 12, window.innerWidth - width - 12)),
      top: rect.top + rect.height / 2,
    })
  }
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="dash-config-warning-btn"
        aria-label={`${issues.join('. ')}. ${action}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? tooltipId : undefined}
        onMouseEnter={(event) => show(event.currentTarget)}
        onMouseLeave={closeSoon}
        onFocus={(event) => show(event.currentTarget)}
        onBlur={closeSoon}
        onKeyDown={(event) => {
          if (open && (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey))) {
            event.preventDefault()
            popupRef.current?.querySelector('button')?.focus()
          }
        }}
        onClick={() => { setPosition(null); onSelect(target) }}
      >
        <IconAlertTriangle size={13} aria-hidden="true" />
      </button>
      {position ? createPortal(
        <div ref={popupRef} id={tooltipId} role="dialog" aria-modal="false" aria-label={t('nav.configWarning.title')}
          className="dash-config-warning-tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
          onFocus={cancelClose}
          onBlur={closeSoon}
          style={{
          left: position.left,
          top: position.top,
        }}>
          <strong>{t('nav.configWarning.title')}</strong>
          <ul>{issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
          <button type="button" className="dash-config-warning-action" onClick={() => { cancelClose(); setPosition(null); onSelect(target) }}>
            {action} <IconArrowRight size={13} aria-hidden="true" />
          </button>
        </div>, document.body,
      ) : null}
    </>
  )
}
